import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  PIPE_SIZES,
  type ApplyDefaultsResultDto,
  type BackfillGeometryDto,
  type BackfillLayerDto,
  type BackfillSetupDto,
  type CostReportDto,
  type CostSetupDto,
  type PipeDto,
  type PipeRunDto,
} from '../lib/api';
import { Button, Card, ErrorAlert, Field, NumberInput, Select } from '../lib/ui';

type Tab = 'setup' | 'pipes' | 'backfill' | 'summary';

const TABS: { id: Tab; label: string }[] = [
  { id: 'setup', label: 'Cost Setup' },
  { id: 'pipes', label: 'Pipes' },
  { id: 'backfill', label: 'Backfill' },
  { id: 'summary', label: 'Cost Summary' },
];

export function ProjectCostingPanel({ projectId }: { projectId: number }) {
  const [tab, setTab] = useState<Tab>('setup');
  const qc = useQueryClient();
  const apply = useMutation({
    mutationFn: () =>
      api.post<ApplyDefaultsResultDto>(`/api/v1/project-folders/${projectId}/apply-user-defaults`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cost-setup', projectId] });
      qc.invalidateQueries({ queryKey: ['project-pipes', projectId] });
      qc.invalidateQueries({ queryKey: ['project-backfill', projectId] });
      qc.invalidateQueries({ queryKey: ['cost-report', projectId] });
    },
  });
  return (
    <Card title="Costing" actions={
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          disabled={apply.isPending}
          onClick={() => {
            if (!confirm('Copy your saved defaults onto this project? This overwrites cost-setup; pipe rates and backfill costs are merged per size/side.')) return;
            apply.mutate();
          }}
          title="Copy your /settings defaults onto this project"
        >
          {apply.isPending ? 'Applying…' : 'Apply my defaults'}
        </Button>
        <div className="flex gap-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`rounded-md px-2.5 py-1 text-xs font-medium ${
                tab === t.id ? 'bg-blue-600 text-white' : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
    }>
      {tab === 'setup' && <CostSetupTab projectId={projectId} />}
      {tab === 'pipes' && <PipesTab projectId={projectId} />}
      {tab === 'backfill' && <BackfillTab projectId={projectId} />}
      {tab === 'summary' && <CostSummaryTab projectId={projectId} />}
    </Card>
  );
}

// =============================================================================
// Setup tab
// =============================================================================

const SETUP_KEY = (id: number) => ['cost-setup', id] as const;

function CostSetupTab({ projectId }: { projectId: number }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: SETUP_KEY(projectId),
    queryFn: () => api.get<CostSetupDto>(`/api/v1/project-folders/${projectId}/cost-setup`),
  });
  const [form, setForm] = useState<CostSetupDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (q.data && form === null) setForm(q.data); }, [q.data, form]);

  const m = useMutation({
    mutationFn: (body: CostSetupDto) =>
      api.put<CostSetupDto>(`/api/v1/project-folders/${projectId}/cost-setup`, body),
    onSuccess: (data) => {
      qc.setQueryData(SETUP_KEY(projectId), data);
      qc.invalidateQueries({ queryKey: ['cost-report', projectId] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Save failed'),
  });

  if (q.isLoading || !form) return <p className="text-sm text-slate-500">Loading…</p>;

  const set = <K extends keyof CostSetupDto>(k: K, v: CostSetupDto[K]) =>
    setForm((f) => (f ? { ...f, [k]: v } : f));

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-3">
        <Field label="ADS stick length (20')"><NumberInput value={form.adsStickLength20} onChange={(e) => set('adsStickLength20', num(e.target.value))} /></Field>
        <Field label="ADS stick length (13')"><NumberInput value={form.adsStickLength13} onChange={(e) => set('adsStickLength13', num(e.target.value))} /></Field>
        <Field label="Other stick length"><NumberInput value={form.otherStickLength} onChange={(e) => set('otherStickLength', num(e.target.value))} /></Field>

        <Field label="ADS crew cost / day"><NumberInput value={form.adsCrewCost} onChange={(e) => set('adsCrewCost', num(e.target.value))} /></Field>
        <Field label="Other crew cost / day"><NumberInput value={form.otherCrewCost} onChange={(e) => set('otherCrewCost', num(e.target.value))} /></Field>
        <Field label="Use nested truckloads">
          <Select value={form.useNestedTruckloads ? '1' : '0'} onChange={(e) => set('useNestedTruckloads', e.target.value === '1')}>
            <option value="0">No</option>
            <option value="1">Yes</option>
          </Select>
        </Field>

        <Field label="ADS time / truck (min)"><NumberInput value={form.adsTimePerTruckMinutes} onChange={(e) => set('adsTimePerTruckMinutes', num(e.target.value))} /></Field>
        <Field label="ADS labor rate"><NumberInput value={form.adsLaborRate} onChange={(e) => set('adsLaborRate', num(e.target.value))} /></Field>
        <div />

        <Field label="Other time / truck (min)"><NumberInput value={form.otherTimePerTruckMinutes} onChange={(e) => set('otherTimePerTruckMinutes', num(e.target.value))} /></Field>
        <Field label="Other labor rate"><NumberInput value={form.otherLaborRate} onChange={(e) => set('otherLaborRate', num(e.target.value))} /></Field>
        <div />
      </div>
      <ErrorAlert message={error} />
      <div className="flex justify-end">
        <Button disabled={m.isPending} onClick={() => { setError(null); m.mutate(form); }}>
          {m.isPending ? 'Saving…' : 'Save setup'}
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// Pipes tab — per-size grid with 20' and 13' runs
// =============================================================================

const PIPES_KEY = (id: number) => ['cost-pipes', id] as const;

function emptyPipe(size: number): PipeDto {
  return {
    sizeInches: size,
    adsInstallRate: 0,
    otherInstallRate: 0,
    otherCostPerFoot: 0,
    otherTruckloadFt: 0,
    runs: [
      emptyRun(20, size),
      emptyRun(13, size),
    ],
  };
}
function emptyRun(stick: number, size: number): PipeRunDto {
  return {
    stickLengthFt: stick,
    totalLengthFt: 0,
    adsCostPerFoot: 0,
    adsTruckloadFt: 0,
    adsSticksPerTruck: 0,
    adsMinParentSize: size,
  };
}

function PipesTab({ projectId }: { projectId: number }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: PIPES_KEY(projectId),
    queryFn: () => api.get<PipeDto[]>(`/api/v1/project-folders/${projectId}/pipes`),
  });
  const [pipes, setPipes] = useState<PipeDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!q.data || pipes !== null) return;
    // Hydrate full grid: ensure every size has a row, with both 20' and 13' runs.
    const bySize = new Map(q.data.map((p) => [p.sizeInches, p]));
    const hydrated = PIPE_SIZES.map((sz) => {
      const found = bySize.get(sz);
      if (!found) return emptyPipe(sz);
      const has20 = found.runs.find((r) => r.stickLengthFt >= 16);
      const has13 = found.runs.find((r) => r.stickLengthFt < 16);
      const runs = [
        has20 ?? emptyRun(20, sz),
        has13 ?? emptyRun(13, sz),
      ];
      return { ...found, runs };
    });
    setPipes(hydrated);
  }, [q.data, pipes]);

  const m = useMutation({
    mutationFn: (body: PipeDto[]) => api.put<PipeDto[]>(`/api/v1/project-folders/${projectId}/pipes`, body),
    onSuccess: (data) => {
      qc.setQueryData(PIPES_KEY(projectId), data);
      qc.invalidateQueries({ queryKey: ['cost-report', projectId] });
      // Re-hydrate after save.
      setPipes(null);
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Save failed'),
  });

  if (q.isLoading || !pipes) return <p className="text-sm text-slate-500">Loading…</p>;

  const updPipe = (i: number, patch: Partial<PipeDto>) =>
    setPipes((arr) => arr!.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const updRun = (i: number, runIdx: number, patch: Partial<PipeRunDto>) =>
    setPipes((arr) => arr!.map((p, idx) => idx === i
      ? { ...p, runs: p.runs.map((r, ri) => (ri === runIdx ? { ...r, ...patch } : r)) }
      : p));

  // Filter on submit: keep only rows that have any non-zero data.
  const submit = () => {
    setError(null);
    const filtered = pipes!
      .map((p) => ({
        ...p,
        runs: p.runs.filter((r) => r.totalLengthFt > 0 || r.adsCostPerFoot > 0),
      }))
      .filter((p) =>
        p.adsInstallRate > 0 || p.otherInstallRate > 0 || p.otherCostPerFoot > 0 || p.runs.length > 0);
    m.mutate(filtered);
  };

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-2 py-2 text-left">Size (in)</th>
              <th className="px-2 py-2 text-left">Stick (ft)</th>
              <th className="px-2 py-2 text-right">Total length (ft)</th>
              <th className="px-2 py-2 text-right">ADS $/ft</th>
              <th className="px-2 py-2 text-right">ADS truckload (ft)</th>
              <th className="px-2 py-2 text-right">ADS sticks/truck</th>
              <th className="px-2 py-2 text-right">ADS min parent</th>
              <th className="px-2 py-2 text-right">Other $/ft</th>
              <th className="px-2 py-2 text-right">Other truckload (ft)</th>
              <th className="px-2 py-2 text-right">ADS install rate</th>
              <th className="px-2 py-2 text-right">Other install rate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {pipes.map((p, i) => (
              p.runs.map((r, ri) => (
                <tr key={`${p.sizeInches}-${r.stickLengthFt}-${ri}`} className="hover:bg-slate-50">
                  {ri === 0 && (
                    <td rowSpan={p.runs.length} className="px-2 py-1 align-top font-medium text-slate-700">
                      {p.sizeInches}″
                    </td>
                  )}
                  <td className="px-2 py-1 text-slate-500">{r.stickLengthFt}</td>
                  <td className="px-2 py-1"><NumberInput value={r.totalLengthFt} onChange={(e) => updRun(i, ri, { totalLengthFt: num(e.target.value) })} className="text-right" /></td>
                  <td className="px-2 py-1"><NumberInput value={r.adsCostPerFoot} onChange={(e) => updRun(i, ri, { adsCostPerFoot: num(e.target.value) })} className="text-right" /></td>
                  <td className="px-2 py-1"><NumberInput value={r.adsTruckloadFt} onChange={(e) => updRun(i, ri, { adsTruckloadFt: num(e.target.value) })} className="text-right" /></td>
                  <td className="px-2 py-1"><NumberInput value={r.adsSticksPerTruck} onChange={(e) => updRun(i, ri, { adsSticksPerTruck: int(e.target.value) })} className="text-right" /></td>
                  <td className="px-2 py-1"><NumberInput value={r.adsMinParentSize} onChange={(e) => updRun(i, ri, { adsMinParentSize: int(e.target.value) })} className="text-right" /></td>
                  {ri === 0 && (
                    <>
                      <td rowSpan={p.runs.length} className="px-2 py-1 align-top"><NumberInput value={p.otherCostPerFoot} onChange={(e) => updPipe(i, { otherCostPerFoot: num(e.target.value) })} className="text-right" /></td>
                      <td rowSpan={p.runs.length} className="px-2 py-1 align-top"><NumberInput value={p.otherTruckloadFt} onChange={(e) => updPipe(i, { otherTruckloadFt: num(e.target.value) })} className="text-right" /></td>
                      <td rowSpan={p.runs.length} className="px-2 py-1 align-top"><NumberInput value={p.adsInstallRate} onChange={(e) => updPipe(i, { adsInstallRate: num(e.target.value) })} className="text-right" /></td>
                      <td rowSpan={p.runs.length} className="px-2 py-1 align-top"><NumberInput value={p.otherInstallRate} onChange={(e) => updPipe(i, { otherInstallRate: num(e.target.value) })} className="text-right" /></td>
                    </>
                  )}
                </tr>
              ))
            ))}
          </tbody>
        </table>
      </div>
      <ErrorAlert message={error} />
      <div className="flex justify-end">
        <Button disabled={m.isPending} onClick={submit}>{m.isPending ? 'Saving…' : 'Save pipes'}</Button>
      </div>
    </div>
  );
}

// =============================================================================
// Backfill tab
// =============================================================================

const BACKFILL_KEY = (id: number) => ['cost-backfill', id] as const;

function emptyLayer(): BackfillLayerDto { return { cost: 0, density: 0, wastePct: 0, isNative: false }; }
function emptyGeometry(size: number): BackfillGeometryDto {
  return {
    sizeInches: size, outerDiameter: 0, trenchWidth: 0, totalLength: 0,
    laidBackBedding: 0, laidBackSpringline: 0, laidBackTopOfPipe: 0,
    laidBackSixOver: 0, laidBackTwelveOver: 0,
  };
}
function emptySide(side: 'ads' | 'other'): BackfillSetupDto {
  return {
    side,
    product: side === 'ads' ? 'N-12' : 'RCP',
    unit: 'Imperial',
    trenchBoxMode: 'none',
    spoilDisposalRate: 0,
    bedding: emptyLayer(),
    springline: emptyLayer(),
    topOfPipe: emptyLayer(),
    sixOver: emptyLayer(),
    twelveOver: emptyLayer(),
    geometry: PIPE_SIZES.map((sz) => emptyGeometry(sz)),
  };
}

function BackfillTab({ projectId }: { projectId: number }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: BACKFILL_KEY(projectId),
    queryFn: () => api.get<BackfillSetupDto[]>(`/api/v1/project-folders/${projectId}/backfill`),
  });
  const [sides, setSides] = useState<{ ads: BackfillSetupDto; other: BackfillSetupDto } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!q.data || sides !== null) return;
    const ads = q.data.find((s) => s.side === 'ads') ?? emptySide('ads');
    const other = q.data.find((s) => s.side === 'other') ?? emptySide('other');
    setSides({ ads: hydrateGeometry(ads), other: hydrateGeometry(other) });
  }, [q.data, sides]);

  const m = useMutation({
    mutationFn: (body: BackfillSetupDto[]) =>
      api.put<BackfillSetupDto[]>(`/api/v1/project-folders/${projectId}/backfill`, body),
    onSuccess: (data) => {
      qc.setQueryData(BACKFILL_KEY(projectId), data);
      qc.invalidateQueries({ queryKey: ['cost-report', projectId] });
      setSides(null);
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Save failed'),
  });

  if (q.isLoading || !sides) return <p className="text-sm text-slate-500">Loading…</p>;

  const submit = () => {
    setError(null);
    m.mutate([sides.ads, sides.other]);
  };

  return (
    <div className="space-y-6">
      <SideEditor
        title="ADS"
        side={sides.ads}
        onChange={(s) => setSides((cur) => ({ ...cur!, ads: s }))}
      />
      <SideEditor
        title="Other"
        side={sides.other}
        onChange={(s) => setSides((cur) => ({ ...cur!, other: s }))}
      />
      <ErrorAlert message={error} />
      <div className="flex justify-end">
        <Button disabled={m.isPending} onClick={submit}>{m.isPending ? 'Saving…' : 'Save backfill'}</Button>
      </div>
    </div>
  );
}

function hydrateGeometry(side: BackfillSetupDto): BackfillSetupDto {
  const bySize = new Map(side.geometry.map((g) => [g.sizeInches, g]));
  return {
    ...side,
    geometry: PIPE_SIZES.map((sz) => bySize.get(sz) ?? emptyGeometry(sz)),
  };
}

const LAYERS: { key: keyof Pick<BackfillSetupDto,'bedding'|'springline'|'topOfPipe'|'sixOver'|'twelveOver'>; label: string }[] = [
  { key: 'bedding', label: 'Bedding' },
  { key: 'springline', label: 'Springline' },
  { key: 'topOfPipe', label: 'Top of pipe' },
  { key: 'sixOver', label: '6" over' },
  { key: 'twelveOver', label: '12" over' },
];

function SideEditor({ title, side, onChange }: {
  title: string;
  side: BackfillSetupDto;
  onChange: (s: BackfillSetupDto) => void;
}) {
  const set = <K extends keyof BackfillSetupDto>(k: K, v: BackfillSetupDto[K]) =>
    onChange({ ...side, [k]: v });
  const setLayer = (k: typeof LAYERS[number]['key'], patch: Partial<BackfillLayerDto>) =>
    onChange({ ...side, [k]: { ...side[k], ...patch } });
  const setGeom = (idx: number, patch: Partial<BackfillGeometryDto>) =>
    onChange({ ...side, geometry: side.geometry.map((g, i) => (i === idx ? { ...g, ...patch } : g)) });

  return (
    <div className="rounded-md border border-slate-200 p-4">
      <h3 className="mb-3 text-sm font-semibold text-slate-800">{title}</h3>
      <div className="grid gap-3 md:grid-cols-4">
        <Field label="Product">
          <Select value={side.product} onChange={(e) => set('product', e.target.value)}>
            <option value="N-12">N-12</option>
            <option value="HP Storm">HP Storm</option>
            <option value="RCP">RCP</option>
            <option value="CMP">CMP</option>
            <option value="other">Other</option>
          </Select>
        </Field>
        <Field label="Unit">
          <Select value={side.unit} onChange={(e) => set('unit', e.target.value)}>
            <option value="Imperial">Imperial</option>
            <option value="Metric">Metric</option>
          </Select>
        </Field>
        <Field label="Trench box">
          <Select value={side.trenchBoxMode} onChange={(e) => set('trenchBoxMode', e.target.value)}>
            <option value="none">None</option>
            <option value="ads">ADS only</option>
            <option value="both">Both</option>
          </Select>
        </Field>
        <Field label="Spoil disposal rate">
          <NumberInput value={side.spoilDisposalRate} onChange={(e) => set('spoilDisposalRate', num(e.target.value))} />
        </Field>
      </div>

      <h4 className="mt-5 mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Layers</h4>
      <div className="overflow-x-auto rounded border border-slate-200">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-2 py-2 text-left">Layer</th>
              <th className="px-2 py-2 text-right">Cost</th>
              <th className="px-2 py-2 text-right">Density</th>
              <th className="px-2 py-2 text-right">Waste %</th>
              <th className="px-2 py-2 text-center">Native</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {LAYERS.map((L) => {
              const layer = side[L.key];
              return (
                <tr key={L.key}>
                  <td className="px-2 py-1 font-medium text-slate-700">{L.label}</td>
                  <td className="px-2 py-1"><NumberInput value={layer.cost} onChange={(e) => setLayer(L.key, { cost: num(e.target.value) })} className="text-right" /></td>
                  <td className="px-2 py-1"><NumberInput value={layer.density} onChange={(e) => setLayer(L.key, { density: num(e.target.value) })} className="text-right" /></td>
                  <td className="px-2 py-1"><NumberInput value={layer.wastePct} onChange={(e) => setLayer(L.key, { wastePct: num(e.target.value) })} className="text-right" /></td>
                  <td className="px-2 py-1 text-center"><input type="checkbox" checked={layer.isNative} onChange={(e) => setLayer(L.key, { isNative: e.target.checked })} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h4 className="mt-5 mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Geometry</h4>
      <div className="overflow-x-auto rounded border border-slate-200">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-2 py-2 text-left">Size</th>
              <th className="px-2 py-2 text-right">OD</th>
              <th className="px-2 py-2 text-right">Trench W</th>
              <th className="px-2 py-2 text-right">Total length</th>
              <th className="px-2 py-2 text-right">LB bed</th>
              <th className="px-2 py-2 text-right">LB spring</th>
              <th className="px-2 py-2 text-right">LB top</th>
              <th className="px-2 py-2 text-right">LB 6"</th>
              <th className="px-2 py-2 text-right">LB 12"</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {side.geometry.map((g, i) => (
              <tr key={g.sizeInches}>
                <td className="px-2 py-1 font-medium text-slate-700">{g.sizeInches}″</td>
                <td className="px-2 py-1"><NumberInput value={g.outerDiameter} onChange={(e) => setGeom(i, { outerDiameter: num(e.target.value) })} className="text-right" /></td>
                <td className="px-2 py-1"><NumberInput value={g.trenchWidth} onChange={(e) => setGeom(i, { trenchWidth: num(e.target.value) })} className="text-right" /></td>
                <td className="px-2 py-1"><NumberInput value={g.totalLength} onChange={(e) => setGeom(i, { totalLength: num(e.target.value) })} className="text-right" /></td>
                <td className="px-2 py-1"><NumberInput value={g.laidBackBedding} onChange={(e) => setGeom(i, { laidBackBedding: num(e.target.value) })} className="text-right" /></td>
                <td className="px-2 py-1"><NumberInput value={g.laidBackSpringline} onChange={(e) => setGeom(i, { laidBackSpringline: num(e.target.value) })} className="text-right" /></td>
                <td className="px-2 py-1"><NumberInput value={g.laidBackTopOfPipe} onChange={(e) => setGeom(i, { laidBackTopOfPipe: num(e.target.value) })} className="text-right" /></td>
                <td className="px-2 py-1"><NumberInput value={g.laidBackSixOver} onChange={(e) => setGeom(i, { laidBackSixOver: num(e.target.value) })} className="text-right" /></td>
                <td className="px-2 py-1"><NumberInput value={g.laidBackTwelveOver} onChange={(e) => setGeom(i, { laidBackTwelveOver: num(e.target.value) })} className="text-right" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// Cost Summary tab — read-only report
// =============================================================================

function CostSummaryTab({ projectId }: { projectId: number }) {
  const q = useQuery({
    queryKey: ['cost-report', projectId],
    queryFn: () => api.get<CostReportDto>(`/api/v1/project-folders/${projectId}/cost-report`),
  });

  if (q.isLoading) return <p className="text-sm text-slate-500">Computing…</p>;
  if (q.error || !q.data) return <p className="text-sm text-red-600">Failed to load report.</p>;

  const r = q.data;
  return (
    <div className="space-y-5">
      <div className="grid gap-4 md:grid-cols-3">
        <Stat title="Pipe material"
          ads={r.pipe.adsTotal} other={r.pipe.otherTotal}
          savings={r.pipe.savingsTotal} pct={r.pipe.reductionPercent} />
        <Stat title="Crew labor"
          ads={r.crew.adsTotal} other={r.crew.otherTotal}
          savings={r.crew.savings} pct={r.crew.reductionPercent} />
        <Stat title="Project total"
          ads={r.totals.adsTotal} other={r.totals.otherTotal}
          savings={r.totals.savings} pct={r.totals.reductionPercent} highlight />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <MetricBox label="Joints"
          rows={[['ADS', r.joints.adsTotal], ['Other', r.joints.otherTotal]]} />
        <MetricBox label="Install days"
          rows={[['ADS', r.days.adsDays], ['Other', r.days.otherDays]]} />
        <MetricBox label="Truck deliveries"
          rows={[
            ['ADS trucks', r.delivery.adsTrucks],
            ['Other trucks', r.delivery.otherTrucks],
            ['ADS delivery cost', money(r.delivery.adsTotal)],
            ['Other delivery cost', money(r.delivery.otherTotal)],
          ]} />
        <MetricBox label="Backfill totals"
          rows={[
            ['ADS', money(r.adsBackfill.total)],
            ['Other', money(r.otherBackfill.total)],
          ]} />
      </div>

      <BackfillBySizeTable ads={r.adsBackfill.bySize} other={r.otherBackfill.bySize} />
    </div>
  );
}

function Stat({ title, ads, other, savings, pct, highlight }: {
  title: string; ads: number; other: number; savings: number; pct: number; highlight?: boolean;
}) {
  return (
    <div className={`rounded-lg border p-4 ${highlight ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{title}</p>
      <dl className="mt-2 space-y-1 text-sm">
        <div className="flex justify-between"><dt className="text-slate-600">ADS</dt><dd className="font-medium tabular-nums">{money(ads)}</dd></div>
        <div className="flex justify-between"><dt className="text-slate-600">Other</dt><dd className="font-medium tabular-nums">{money(other)}</dd></div>
        <div className="flex justify-between border-t border-slate-200 pt-1">
          <dt className="text-slate-600">Savings</dt>
          <dd className={`font-semibold tabular-nums ${savings >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{money(savings)}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-slate-600">Reduction</dt>
          <dd className="font-medium tabular-nums">{pct}%</dd>
        </div>
      </dl>
    </div>
  );
}

function MetricBox({ label, rows }: { label: string; rows: [string, number | string][] }) {
  return (
    <div className="rounded-md border border-slate-200 p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <dl className="mt-2 space-y-1 text-sm">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between">
            <dt className="text-slate-600">{k}</dt>
            <dd className="font-medium tabular-nums">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function BackfillBySizeTable({ ads, other }: { ads: Record<string, number>; other: Record<string, number> }) {
  const rows = useMemo(() =>
    PIPE_SIZES.map((sz) => ({
      sz,
      ads: ads[String(sz)] ?? 0,
      other: other[String(sz)] ?? 0,
    })).filter((r) => r.ads !== 0 || r.other !== 0),
  [ads, other]);
  if (rows.length === 0) return null;

  return (
    <div className="rounded-md border border-slate-200">
      <p className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Backfill cost by size</p>
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 text-xs text-slate-600">
          <tr>
            <th className="px-3 py-2 text-left">Size</th>
            <th className="px-3 py-2 text-right">ADS</th>
            <th className="px-3 py-2 text-right">Other</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => (
            <tr key={r.sz}>
              <td className="px-3 py-1.5 font-medium text-slate-700">{r.sz}″</td>
              <td className="px-3 py-1.5 text-right tabular-nums">{money(r.ads)}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">{money(r.other)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function num(v: string): number {
  if (v === '' || v === '-') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
function int(v: string): number {
  const n = Math.trunc(num(v));
  return Number.isFinite(n) ? n : 0;
}
function money(n: number): string {
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}
